import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import LoadMore from "@components/LoadMore/LoadMore";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import {
  AwardType,
  EventsAwardsGalleryContextData,
} from "@events-awards/utils/types";
import { useLoadMoreEventsGallery } from "@hooks/useLoadMoreEventsGallery";
import { cn } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

import EventsAwardsHeader from "./EventAwardsHeader";
import GalleryItem from "./EventsAwardsGalleryItem";

/**
 * Component to render gallery page for event and awards
 */
const EventsAwardsGalleryPageContent = ({
  title,
  articles,
  awardType,
  year,
}: EventsAwardsGalleryContextData): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();
  const { articleList, handleLoadMore, hasMoreArticles } =
    useLoadMoreEventsGallery(articles, 10, awardType, year);

  const awardData = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";
  const isSIAAward = awardType === AwardType.SIA;

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-awards-gallery-page-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/gallery`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Gallery`}
        level2="events awards"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <EventsAwardsHeader />

      <Container>
        <Row>
          <Column rootClassName="w-full">
            <EventsAwardsLogoMenu awardType={awardType} />

            {isSIAAward ? (
              <BlockTitle
                text="Gallery"
                rootClassName="text-4xl md:text-6xl lg:text-8xl font-medium text-left"
              />
            ) : null}

            <div className={cn("py-8", { "mx-auto max-w-4xl": !isSIAAward })}>
              <div
                className={cn("grid grid-cols-1 gap-6 md:grid-cols-2", {
                  "grid-cols-2 gap-4 lg:grid-cols-4": isSIAAward,
                })}
              >
                {articleList?.map(({ id, title, elements, urlPath }) => (
                  <GalleryItem
                    key={id || title}
                    link={urlPath}
                    title={title}
                    elements={elements}
                  />
                ))}
              </div>

              {hasMoreArticles ? (
                <div className="mt-8">
                  <LoadMore
                    onLoadMore={handleLoadMore}
                    hasMore={hasMoreArticles}
                    loadText="Show More"
                  />
                </div>
              ) : null}
            </div>
          </Column>
        </Row>
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsGalleryPageContent;
